class CartRewards extends HTMLElement {
  constructor() {
    super();
    this._draftValue = '';
    this._hadFocus = false;
  }

  connectedCallback() {
    // Delegated on the root, which survives every morph — the children do not.
    this.addEventListener('click', (event) => {
      const applyButton = event.target.closest('[data-rewards-apply-input]');
      if (applyButton) {
        this._redeem(this._input()?.value, applyButton);
        return;
      }

      const tier = event.target.closest('[data-reward-tier]');
      if (!tier || tier.dataset.locked === 'true') return;
      this._redeem(tier.dataset.coins, event.target.closest('button'));
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || !event.target.closest('[data-cart-rewards-input]')) return;
      event.preventDefault();
      this._redeem(event.target.value, this.querySelector('[data-rewards-apply-input]'));
    });

    this.addEventListener('input', (event) => {
      if (!event.target.closest('[data-cart-rewards-input]')) return;
      const digits = event.target.value.replace(/\D/g, '');
      if (digits !== event.target.value) event.target.value = digits;
      this._draftValue = event.target.value;
    });

    // `cart:mutation-end` is global, so focus is only reclaimed when this region
    // owned it before the morph — otherwise a line-quantity bump would yank it here.
    this.handleMutationStart = () => {
      this._hadFocus = this.contains(document.activeElement);
    };
    document.addEventListener('cart:mutation-start', this.handleMutationStart);

    this.handleMutationEnd = () => this._handleMutationEnd();
    document.addEventListener('cart:mutation-end', this.handleMutationEnd);

    this._syncLockedTiers();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:mutation-start', this.handleMutationStart);
    document.removeEventListener('cart:mutation-end', this.handleMutationEnd);
  }

  // The adapter mints the code; `<cart-promo>` owns everything after that — the
  // request, the applied/rejected verdict, its own error and the restore path.
  async _redeem(rawAmount, control = null) {
    const amount = this._clamp(rawAmount);
    if (!amount) return;

    const code = await window.CartRewardsAdapter?.requestRedemptionCode?.(amount);
    if (!code) {
      CartMutations.showError(this.closest('cart-drawer'), { control: control ?? this });
      return;
    }

    // `cart_enable_rewards` and `cart_enable_promo` are independent settings, so the
    // apply path can genuinely be absent. Surface it rather than swallowing the click.
    const promo = document.querySelector('cart-promo');
    if (!promo) {
      CartMutations.showError(this.closest('cart-drawer'), { control: control ?? this });
      return;
    }

    await promo.applyCode(code, control);

    this._draftValue = '';
    this._clearInput();
  }

  _clamp(raw) {
    const digits = String(raw ?? '').replace(/\D/g, '');
    const amount = Number.parseInt(digits, 10) || 0;
    const max = Number(this.dataset.max) || 0;
    return Math.min(amount, max);
  }

  // Locked state is expressed on the card's existing button rather than in
  // `coupon-card.liquid`, which stays generic and unedited.
  _syncLockedTiers() {
    for (const tier of this.querySelectorAll('[data-reward-tier]')) {
      const button = tier.querySelector('button[data-promo-apply-code]');
      if (!button) continue;

      const isLocked = tier.dataset.locked === 'true';
      button.disabled = isLocked;
      if (isLocked) button.setAttribute('aria-label', tier.dataset.lockedLabel ?? '');
    }
  }

  // The morph repaints this region unlocked and empty, so both are restored here.
  _handleMutationEnd() {
    this._syncLockedTiers();

    const input = this._input();
    if (!input || !this._draftValue || input.value) return;

    input.value = this._draftValue;
    if (!this._hadFocus) return;

    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }

  _input() {
    return this.querySelector('[data-cart-rewards-input]');
  }

  _clearInput() {
    const input = this._input();
    if (input) input.value = '';
  }
}

if (!customElements.get('cart-rewards')) {
  customElements.define('cart-rewards', CartRewards);
}
